import React, { createContext, useContext, useState, useCallback } from 'react';

interface ExpansionContextType {
  expandedItems: Record<string, boolean>;
  toggleExpanded: (id: string) => void;
  setExpanded: (id: string, expanded: boolean) => void;
  expandMultiple: (ids: string[]) => void;
  collapseAll: () => void;
  collapseAllExceptYears: () => void;
  expandProject: (projectId: string, pulseIds?: number[]) => void;
  expandPulse: (pulseId: number) => void;
  expandPulsesWithIncompleteTasks: (pulses: any[]) => void;
}

const ExpansionContext = createContext<ExpansionContextType | undefined>(undefined);

export function ExpansionProvider({ children }: { children: React.ReactNode }) {
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});

  const toggleExpanded = useCallback((id: string) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  }, []);

  const setExpanded = useCallback((id: string, expanded: boolean) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: expanded
    }));
  }, []);

  const expandMultiple = useCallback((ids: string[]) => {
    setExpandedItems(prev => {
      const newState = { ...prev };
      ids.forEach(id => {
        newState[id] = true;
      });
      return newState;
    });
  }, []);

  const collapseAll = useCallback(() => {
    setExpandedItems({});
  }, []);

  // Collapses projects, pulses, tasks, and any non-year keys while preserving
  // the user's year-level expansion. Used by the ESC reset shortcut so a
  // double-tap collapses content rows without re-hiding the year sections
  // the user deliberately opened (e.g. their current year).
  const collapseAllExceptYears = useCallback(() => {
    setExpandedItems(prev => {
      const next: Record<string, boolean> = {};
      for (const [key, value] of Object.entries(prev)) {
        if (value && key.startsWith('year-')) {
          next[key] = true;
        }
      }
      return next;
    });
  }, []);

  const expandProject = useCallback((projectId: string, pulseIds?: number[]) => {
    setExpandedItems(prev => {
      const newState = { ...prev };
      // Expand the project
      newState[projectId] = true;
      
      // If pulse IDs are provided, expand those specific pulses
      if (pulseIds && pulseIds.length > 0) {
        pulseIds.forEach(pulseId => {
          newState[`pulse-${pulseId}`] = true;
        });
      }
      
      return newState;
    });
  }, []);

  const expandPulse = useCallback((pulseId: number) => {
    setExpandedItems(prev => ({
      ...prev,
      [`pulse-${pulseId}`]: true
    }));
  }, []);

  const expandPulsesWithIncompleteTasks = useCallback((pulses: any[]) => {
    setExpandedItems(prev => {
      const newState = { ...prev };
      
      pulses.forEach(pulse => {
        // Check if pulse has tasks and if any task is not done
        if (pulse.tasks && pulse.tasks.length > 0) {
          const hasIncompleteTasks = pulse.tasks.some((task: any) => 
            task.status !== 'done' && task.status !== 'Done'
          );
          
          // Expand pulse if it has incomplete tasks
          if (hasIncompleteTasks) {
            newState[`pulse-${pulse.id}`] = true;
          }
        }
      });
      
      return newState;
    });
  }, []);

  return (
    <ExpansionContext.Provider value={{
      expandedItems,
      toggleExpanded,
      setExpanded,
      expandMultiple,
      collapseAll,
      collapseAllExceptYears,
      expandProject,
      expandPulse,
      expandPulsesWithIncompleteTasks
    }}>
      {children}
    </ExpansionContext.Provider>
  );
}

export function useExpansion() {
  const context = useContext(ExpansionContext);
  if (!context) {
    throw new Error('useExpansion must be used within ExpansionProvider');
  }
  return context;
}

/** Like useExpansion, but returns undefined when rendered outside the provider. */
export function useOptionalExpansion() {
  return useContext(ExpansionContext);
}